import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Button,
    Stack,
    Stepper,
    Step,
    StepButton,
    StepContent,
    StepLabel,
    Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ValueDriverDisplay from './ValueDriverDisplay';
import { formatString } from './valueFunctions';
import { editRevenueModel, editVariableValues } from '../../actions/valueActions';
import { removeCommasFromValues } from './valueFunctions';
import ModalElement from '../Modal';
import VariablesForm from './VariablesForm';
import history from '../../history';
import RevenueModelModal from './RevenueModelModal';
import { getIconFromDriverName } from './iconFunctions';

// Mappings for category keys to labels and colors.
const categoryLabels = {
    cost_savings: 'Cost Savings',
    extra_costs: 'Extra Costs',
    revenue_enhancements: 'Revenue Enhancements',
    risk_lowering: 'Risk Lowering',
};

const categoryColors = {
    cost_savings: '#4caf50',         // success
    extra_costs: '#f44336',          // error
    revenue_enhancements: '#1976d2', // primary
    risk_lowering: '#ff9800',        // warning
};

// Custom Step Icon Component: the circle (with step number) uses the category color.
const CustomStepIcon = (props) => {
    const { icon, categoryKey } = props;
    const color = categoryColors[categoryKey] || 'grey';
    return (
        <div
            style={{
                backgroundColor: color,
                borderRadius: '50%',
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '0.8rem',
            }}
        >
            {icon}
        </div>
    );
};

const DriverAnalysisDisplay = ({ id, valueModel, costStructure, driver_analysis }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    // Flatten impact items: add the original category key to each object.
    const steps = [];

    Object.keys(driver_analysis).filter(catKey => catKey !== "revenue_model").forEach((catKey) => {
        (driver_analysis[catKey] || []).forEach((item, index) => {
            steps.push({
                id: `${catKey}-${index}`,
                categoryKey: catKey,
                title: item.title,
                explanation: item.explanation,
                drivers: item.drivers, // array of driver objects
            });
        });
    });

    // Local state for the active step index.
    const [activeStep, setActiveStep] = useState(0);
    // Local state for the selected driver index for the active step.
    const [driverIndex, setDriverIndex] = useState(0);
    // Local state for focus (for the input field below, if any).
    // const [focused, setFocused] = useState(false);
    // Local state for the confirmation Variables modal.
    const [variablesSetOpen, setVariablesSetOpen] = useState(false)

    // Reset the selected driver index when the active step changes.
    useEffect(() => {
        setDriverIndex(0);
    }, [activeStep]);


    // Get current impact item
    const currentImpact = steps[activeStep] || {};

    // Sort drivers so that if "personnel_costs" exists and is applicable, it appears first.
    // We pull the `.drivers || []` inside the memo and depend on the original array reference.
    const sortedDrivers = useMemo(() => {
        const drivers = currentImpact.drivers || [];
        return [...drivers].sort((a, b) => {
            if (a.driver === "personnel_costs" && b.driver !== "personnel_costs") return -1;
            if (b.driver === "personnel_costs" && a.driver !== "personnel_costs") return 1;
            return 0;
        });
    }, [currentImpact.drivers]);

    const handleClose = () => {
        setVariablesSetOpen(false)
    }

    const variablesOnSubmit = (values) => {
        values = removeCommasFromValues(values)
        dispatch(editVariableValues(id, values))
        handleClose()
        history.push(`/value-models?id=${valueModel?.product.id}`)
        if (formikRef.current) {
            formikRef.current.resetForm();
        }

    }

    const revenueModelOnSubmit = (values) => {
        dispatch(editRevenueModel(id, values))
    }

    const variablesSetFormActions = () => {
        return (
            <React.Fragment>
                <Button variant="contained" startIcon={<SaveIcon />} size="small" type="submit" sx={{ mt: 2, textTransform: 'none' }}>Save Value Model</Button>
            </React.Fragment>
        )
    }

    function renderVariablesSetModal() {
        return (
            <ModalElement
                modalOpen={variablesSetOpen}
                modalWidth={400}
                handleClose={handleClose}
                header="Set Default Values before save"
                instruction="Set values that are typical for a company in the industry"
                content={
                    <VariablesForm
                        variables={valueModel?.variables}
                        formActions={variablesSetFormActions}
                        onSubmit={variablesOnSubmit}
                    />
                }
            />
        )
    }

    function renderRevenueModelSetModal() {
        return (
            <RevenueModelModal
                driver_analysis={driver_analysis}
                revenue_model={valueModel?.driver_analysis?.revenue_model}
                onSubmit={revenueModelOnSubmit}
            />
        )
    }

    return (
        <Paper
            sx={{
                p: 3,
                maxWidth: 1800,
                ml: 5,
                mt: 3,
                // m: 'auto',
                // bgcolor: "primary.verylight",
                background: theme =>
                    `linear-gradient(
        to bottom right,
        ${theme.palette.primary.main} 0%,
        ${theme.palette.primary.verylight} 100%
        )`,
                borderRadius: '30px',
                mb: 5
            }}
        >
            {/* Heading */}
            <Typography variant="h5" gutterBottom sx={{ ml: 5, color: 'white' }}>
                Value Driver Analysis
            </Typography>

            <Box sx={{ p: 5 }}>
                <Grid container spacing={2}>
                    {/* Top: Toolbox */}
                    <Grid item xs={12} md={12}>
                        <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="h6" gutterBottom>
                                    Toolbox
                                </Typography>
                                <Button
                                    startIcon={<SaveIcon />}
                                    variant="contained"
                                    sx={{ textTransform: 'none' }}
                                    onClick={() => setVariablesSetOpen(true)}
                                >
                                    Save Value Model
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Left Side: Vertical Stepper */}
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 3, minHeight: 200, backgroundColor: 'background.paper' }}>
                            <Typography variant="h6" gutterBottom>
                                Outcome Analysis
                            </Typography>
                            <Typography variant="caption">
                                Select an Outcome to add Value Drivers on the right
                            </Typography>
                            <Stepper activeStep={activeStep} orientation="vertical" nonLinear sx={{ mt: 3 }}>
                                {steps.map((step, index) => (
                                    <Step key={step.id}>
                                        <StepButton onClick={() => setActiveStep(index)}>
                                            <StepLabel
                                                StepIconComponent={(props) => (
                                                    <CustomStepIcon {...props} categoryKey={step.categoryKey} />
                                                )}
                                            >
                                                <Typography variant="subtitle1">{step.title}</Typography>
                                            </StepLabel>
                                        </StepButton>
                                        {activeStep === index && (
                                            <StepContent>
                                                <Chip
                                                    label={categoryLabels[step.categoryKey]}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: categoryColors[step.categoryKey],
                                                        color: 'white',
                                                        mb: 1,
                                                    }}
                                                />
                                                <Typography variant="body2">{step.explanation}</Typography>
                                            </StepContent>
                                        )}
                                    </Step>
                                ))}
                            </Stepper>
                        </Paper>
                    </Grid>

                    {/* Right Side: Driver Selection & Display */}
                    <Grid item xs={12} md={9}>
                        <Paper sx={{ p: 3, minHeight: 200, backgroundColor: 'background.paper' }}>
                            <Typography variant="h6" gutterBottom>
                                Drivers for {currentImpact.title || "Selected Impact"}
                            </Typography>
                            {sortedDrivers.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {sortedDrivers.map((driverObj, idx) => (
                                        <Chip
                                            key={idx}
                                            label={formatString(driverObj.driver)}
                                            clickable
                                            onClick={() => setDriverIndex(idx)}
                                            color={driverIndex === idx ? "primary" : "default"}
                                            sx={{ cursor: 'pointer' }}
                                            icon={<Box component={getIconFromDriverName(driverObj.driver)} sx={{ fontSize: 18 }} />}
                                        />
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body1">No drivers for this impact area.</Typography>
                            )}
                            {sortedDrivers.length > 0 && sortedDrivers[driverIndex]?.driver && (
                                <Stack direction="column" sx={{ mt: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        Reason
                                    </Typography>
                                    <Typography variant="caption">
                                        {sortedDrivers[driverIndex].justification}
                                    </Typography>
                                </Stack>
                            )}

                            {/* Optionally, display the detailed ValueDriverDisplay component below if needed */}
                            {sortedDrivers.length > 0 && sortedDrivers[driverIndex]?.driver &&
                                <ValueDriverDisplay
                                    id={id}
                                    valueModel={valueModel}
                                    costStructure={costStructure}
                                    driver={sortedDrivers[driverIndex].driver}
                                    outcome={currentImpact.title}
                                    outcome_explanation={currentImpact.explanation}
                                    reason={sortedDrivers[driverIndex].justification}
                                    affectedTypes={sortedDrivers[driverIndex]?.affected_types || []}
                                    driver_analysis={driver_analysis}
                                />
                            }
                        </Paper>
                    </Grid>
                </Grid>
                {renderVariablesSetModal()}
                {renderRevenueModelSetModal()}
            </Box>
        </Paper>
    );
};

export default DriverAnalysisDisplay;
